// Teacher administration
import { RepositoryFactory } from "@/repositories/RepositoryFactory";

const TeachersRepository = RepositoryFactory.get("teachers");

const state = {
  teachers: [],
  teacher: {},
  teacherStudents: [],
  teacherSchools: [],
};

const actions = {
  getTeachers({ dispatch, commit }, params) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    TeachersRepository.listTeachers(params)
      .then((response) => {
        const teachers = response.data.results;
        const totalPages = response.data.total_pages;
        commit("pagination/SET_TOTAL_PAGES", totalPages, { root: true });
        commit("SAVE_TEACHERS", teachers);
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message:
              "Un problème est survenue lors du chargement des professeurs",
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  getTeacher({ dispatch, commit }, id) {
    return TeachersRepository.getTeacher(id)
      .then((response) => {
        const teacher = response.data;
        commit("SAVE_TEACHER", teacher);
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message:
              "Un problème est survenu lors de la récupération des information du professeur",
          },
          { root: true },
        );
      });
  },
  createTeacher({ dispatch, commit }, payload) {
    return TeachersRepository.createTeacher(payload)
      .then((response) => {
        const teacher = response.data;
        commit("SAVE_TEACHER", teacher);
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(
              `message.notifications.teachers.createTeacherSuccess`,
            ),
          },
          { root: true },
        );
      })
      .catch((e) => {
        let errorMessage;
        if (e.response && e.response.status === 400) {
          if (e.response.data.message === "email_already_used") {
            errorMessage = state.$t(
              `message.notifications.teachers.createTeacherEmailError`,
            );
          } else {
            errorMessage = state.$t(
              `message.notifications.teachers.createTeacherPasswordError`,
            );
          }
        } else {
          errorMessage = state.$t(
            `message.notifications.teachers.createTeacherError`,
          );
        }
        dispatch(
          "notifications/addError",
          { message: errorMessage },
          { root: true },
        );

        throw e;
      });
  },
  updateTeacher({ dispatch, commit }, payload) {
    const id = payload.id;
    return TeachersRepository.updateTeacher(id, payload)
      .then((response) => {
        const teacher = response.data;
        commit("SAVE_TEACHER", teacher);
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(
              `message.notifications.teachers.updateTeacherSuccess`,
            ),
          },
          { root: true },
        );
      })
      .catch((e) => {
        let errorMessage;
        if (e.response && e.response.status === 400) {
          if (e.response.data.message === "email_already_used") {
            errorMessage = state.$t(
              `message.notifications.teachers.updateTeacherEmailError`,
            );
          } else {
            errorMessage = state.$t(
              `message.notifications.teachers.updateTeacherPasswordError`,
            );
          }
        } else {
          errorMessage = state.$t(`message.notifications.errorOccuredUpdating`);
        }
        dispatch(
          "notifications/addError",
          { message: errorMessage },
          { root: true },
        );

        throw e;
      });
  },
  subscribe({ dispatch }, id) {
    TeachersRepository.subscribe(id).catch(() => {
      dispatch(
        "notifications/addError",
        {
          message:
            "Une erreur est survenu lors du changement de statut d'abonnement",
        },
        { root: true },
      );
    });
  },
  unsubscribe({ dispatch }, id) {
    TeachersRepository.unsubscribe(id).catch(() => {
      dispatch(
        "notifications/addError",
        {
          message:
            "Une erreur est survenu lors du changement de statut d'abonnement",
        },
        { root: true },
      );
    });
  },
  checkSubscription({ dispatch, state, rootState }) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    let user = rootState.users.user;

    // Create a new Promise because we need to wait for another async call inside the 'then'
    return new Promise((resolve) => {
      if (!user.teacher) {
        resolve(false);
        return;
      }

      TeachersRepository.checkSubscription(user.teacher.id)
        .then((response) => {
          const data = response.data;
          if (data.is_subscribed) {
            // Wait for the "getUser" to complete before resolving
            let payload = {
              username: user.username,
              initSession: false,
            };
            resolve(dispatch("users/getUser", payload, { root: true }));
          } else {
            // Resolve with a 'false' parameter so we don't throw an error (teacher not suscribed != error)
            resolve(false);
          }
        })
        .catch(() => {
          dispatch(
            "notifications/addError",
            {
              message: state.$t(
                `message.notifications.homework.checkSubscriptionFailure`,
              ),
            },
            { root: true },
          );
        })
        .finally(() => {
          dispatch("loadings/disableLoadings", {}, { root: true });
        });
    });
  },
  setTeacherFromList({ commit, state }, teacherId) {
    const teacher = state.teachers.find((t) => t.id === teacherId);
    commit("SAVE_TEACHER", teacher);
  },
  resetCurrentTeacher({ commit }) {
    commit("RESET_TEACHER");
  },
  getTeacherStudents({ commit, dispatch }, teacherId) {
    return TeachersRepository.getTeacherStudents(teacherId)
      .then((response) => {
        commit("SAVE_TEACHER_STUDENTS", response.data);
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(
              `message.notifications.teachers.getTeacherStudentsError`,
            ),
          },
          { root: true },
        );
      });
  },
  getTeacherSchools({ commit, dispatch }, teacherId) {
    TeachersRepository.getTeacherSchools(teacherId)
      .then((response) => {
        commit("SAVE_TEACHER_SCHOOLS", response.data);
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(
              `message.notifications.teachers.getTeacherSchoolsError`,
            ),
          },
          { root: true },
        );
      });
  },
  getStudentsForTeachers({ commit, dispatch }, payload) {
    return TeachersRepository.getStudentsForTeachers(payload)
      .then((response) => {
        commit("SAVE_TEACHER_STUDENTS", response.data);
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(
              `message.notifications.teachers.getTeacherStudentsError`,
            ),
          },
          { root: true },
        );
      });
  },
  setGradingSystem({ commit, dispatch, rootState }, gradingSystemCode) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    let user = rootState.users.user;
    if (!user.teacher) {
      return;
    }

    let payload = {
      grading_system_code: gradingSystemCode,
    };

    TeachersRepository.setGradingSystem(user.teacher.id, payload)
      .then(() => {
        commit("users/SET_TEACHER_GRADING_SYSTEM", gradingSystemCode, {
          root: true,
        });
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(
              `message.notifications.teachers.setGradingSystemSuccess`,
            ),
          },
          { root: true },
        );
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(
              `message.notifications.teachers.setGradingSystemError`,
            ),
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
};

const mutations = {
  SAVE_TEACHERS(state, teachers) {
    state.teachers = teachers;
  },
  UPDATE_TEACHER(state, id, teacher) {
    state.teachers = [
      ...state.teachers.filter((elt) => elt.id !== id),
      teacher,
    ];
  },
  SAVE_TEACHER(state, teacher) {
    state.teacher = teacher;
  },
  RESET_TEACHER(state) {
    state.teacher = {};
  },
  SAVE_TEACHER_STUDENTS(state, teacherStudents) {
    state.teacherStudents = teacherStudents;
  },
  RESET_TEACHER_STUDENTS(state) {
    state.teacherStudents = [];
  },
  SAVE_TEACHER_SCHOOLS(state, schools) {
    state.teacherSchools = schools;
  },
  RESET_TEACHER_SCHOOLS(state) {
    state.teacherSchools = [];
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
